/* eslint-disable */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { MaterialReactTable } from "material-react-table";

import TabsHeader from "components/Common/TabsHeader/TabsHeader";
import { commonHeaderLinkLists } from "helper/WebhookHelper/WebhookHelper";

import BreadCrumb from "components/Common/BreadCrumb/BreadCrumb";
import AlertNotification from "components/Common/AlertNotification/AlertNotification";
import LoadingSkeleton from "components/Common/ListLoadingSkeleton/LoadingSkeleton";
import WebhookCallDetailsPanel from "../WebhookCallDetailsPanel/WebhookCallDetailsPanel";
import WebhookEventFilterPopup from "../Popup/WebhookEventFilterPopup";

import { getData } from "utils/Gateway";
import * as url from "helper/UrlHelper";

const EVENT_TYPE_STYLE = {
  "call.incoming":  { bg: "#dbeafe", color: "#1d4ed8", label: "Incoming" },
  "call.ended":     { bg: "#dcfce7", color: "#15803d", label: "Ended" },
  "call.recording": { bg: "#fef9c3", color: "#92400e", label: "Recording" },
  "call.unknown":   { bg: "#f3f4f6", color: "#6b7280", label: "Unknown" },
};

const PROVIDER_COLORS = {
  retell:   { bg: "#ede9fe", color: "#6d28d9" },
  pbx: { bg: "#ffedd5", color: "#c2410c" },
};

// Group flat event list by callId, keeping the latest event per callId
const groupByCallId = (events) => {
  const map = new Map();
  for (const ev of events) {
    const key = ev.callId || ev._id;
    const existing = map.get(key);
    if (!existing) {
      map.set(key, { ...ev, _eventCount: 1 });
    } else {
      const existingTs = new Date(existing.normalized?.timestamp || existing.createdAt || 0);
      const newTs      = new Date(ev.normalized?.timestamp       || ev.createdAt       || 0);
      // always prefer whichever event has lead/user populated
      const merged = newTs > existingTs
        ? { ...ev,       _eventCount: existing._eventCount + 1 }
        : { ...existing, _eventCount: existing._eventCount + 1 };

      // carry lead and user from whichever event has them
      if (!merged.lead && existing.lead) merged.lead = existing.lead;
      if (!merged.lead && ev.lead)       merged.lead = ev.lead;
      if (!merged.user && existing.user) merged.user = existing.user;
      if (!merged.user && ev.user)       merged.user = ev.user;

      map.set(key, merged);
    }
  }
  return Array.from(map.values()).sort(
    (a, b) =>
      new Date(b.normalized?.timestamp || b.createdAt || 0) -
      new Date(a.normalized?.timestamp || a.createdAt || 0)
  );
};

const WebhookDataBody = () => {
  const { t } = useTranslation();

  const commonHeaderObject = commonHeaderLinkLists(t);
  const breadcrumbText = [
    { title: t("Webhook"), link: "/admin/webhook/list" },
    { title: t("Live Data") },
  ];

  const [webhookEvents, setWebhookEvents] = useState([]);
  const [isLoading, setIsLoading]         = useState(false);
  const [selectedCallId, setSelectedCallId] = useState(null);

  // filters
  const [phoneFilter,     setPhoneFilter]     = useState("");
  const [eventTypeFilter, setEventTypeFilter] = useState("");
  const [providerFilter,  setProviderFilter]  = useState("");
  const [fromDate,        setFromDate]        = useState("");
  const [toDate,          setToDate]          = useState("");

  // alert
  const [showAlert,    setShowAlert]    = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [messageType,  setMessageType]  = useState("");

  const activeFilterCount = [phoneFilter, eventTypeFilter, providerFilter, fromDate, toDate].filter(Boolean).length;

  const groupedEvents = useMemo(() => groupByCallId(webhookEvents), [webhookEvents]);

  const fetchWebhookEvents = async (overrides = {}) => {
    try {
      setIsLoading(true);
      const phone     = overrides.localPhone     !== undefined ? overrides.localPhone     : phoneFilter;
      const eventType = overrides.localEventType !== undefined ? overrides.localEventType : eventTypeFilter;
      const provider  = overrides.localProvider  !== undefined ? overrides.localProvider  : providerFilter;
      const from      = overrides.localFromDate  !== undefined ? overrides.localFromDate  : fromDate;
      const to        = overrides.localToDate    !== undefined ? overrides.localToDate    : toDate;

      let requestUrl = url.API_BASE_URL + url.API_GET_TELEPHONY_EVENTS + "?limit=500";
      if (phone)     requestUrl += `&phone=${encodeURIComponent(phone)}`;
      if (eventType) requestUrl += `&eventType=${encodeURIComponent(eventType)}`;
      if (provider)  requestUrl += `&provider=${encodeURIComponent(provider)}`;
      if (from)      requestUrl += `&fromDate=${from}`;
      if (to)        requestUrl += `&toDate=${to}`;

      const response = await getData(requestUrl);
      if (response.status) {
        setWebhookEvents(response.data || []);
      } else {
        setAlertMessage(response.message);
        setMessageType("error");
        setShowAlert(true);
      }
    } catch (err) {
      setAlertMessage(err.message);
      setMessageType("error");
      setShowAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchWebhookEvents();
  }, []);

  const openCallDetails = (callId) => {
    setSelectedCallId(callId);
    const el = document.querySelector("#webhookCallDetailsPanel");
    if (el) {
      const instance =
        window.bootstrap?.Offcanvas?.getInstance(el) ||
        new window.bootstrap.Offcanvas(el);
      instance.show();
    }
  };

  const handleApplyFilter = (values) => fetchWebhookEvents(values);
  const handleResetFilter = () =>
    fetchWebhookEvents({
      localPhone: "", localEventType: "", localProvider: "",
      localFromDate: "", localToDate: "",
    });

  const onAlertClose = () => {
    setShowAlert(false);
    setAlertMessage("");
    setMessageType("");
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "#",
        header: t("Actions"),
        size: 70,
        enableSorting: false,
        enableColumnActions: false,
        enableColumnDragging: false,
        Cell: ({ row }) => (
          <button
            className="action_btn_mui"
            onClick={() => openCallDetails(row.original.callId || row.original._id)}
          >
            <span className="d-block material-symbols-outlined horz_icon">more_horiz</span>
          </button>
        ),
      },
      {
        accessorKey: "eventType",
        header: t("Latest Status"),
        size: 160,
        Cell: ({ cell }) => {
          const typeStyle = EVENT_TYPE_STYLE[cell.getValue()] || EVENT_TYPE_STYLE["call.unknown"];
          return (
            <span
              className="badge rounded-pill fw-semibold"
              style={{ background: typeStyle.bg, color: typeStyle.color, fontSize: "11px" }}
            >
              {typeStyle.label}
            </span>
          );
        },
      },
      {
        accessorKey: "phone",
        header: t("Phone"),
        size: 150,
        Cell: ({ cell, row }) => {
          const val = cell.getValue() || row.original.normalized?.phone;
          return val ? <span className="font-monospace fs-sm">{val}</span> : "—";
        },
      },
      {
        accessorKey: "callId",
        header: t("Call ID"),
        size: 220,
        Cell: ({ cell }) => (
          <span className="text-muted fs-xs text-truncate d-block" style={{ maxWidth: 200 }}>
            {cell.getValue() || "—"}
          </span>
        ),
      },
      {
        accessorKey: "provider",
        header: t("Provider"),
        size: 120,
        Cell: ({ cell }) => {
          const p = cell.getValue();
          const provStyle = PROVIDER_COLORS[p] || { bg: "#f3f4f6", color: "#374151" };
          return (
            <span
              className="badge rounded-pill fw-semibold text-capitalize"
              style={{ background: provStyle.bg, color: provStyle.color, fontSize: "11px" }}
            >
              {p}
            </span>
          );
        },
      },
      {
        accessorKey: "user",
        header: t("Owner"),
        size: 160,
        Cell: ({ row }) => {
          const u = row.original.user;
          if (!u) return <span className="text-muted fs-xs">—</span>;
          const label = `${u.name || ""} ${u.surname || ""}`.trim() || u.email;
          return (
            <div className="d-flex align-items-center gap-2">
              <span
                className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center flex-shrink-0 fs-xs fw-bold"
                style={{ width: 26, height: 26, fontSize: "10px" }}
              >
                {label.charAt(0).toUpperCase()}
              </span>
              <span className="fs-sm text-truncate" style={{ maxWidth: 120 }} title={label}>
                {label}
              </span>
            </div>
          );
        },
      },
      {
        accessorKey: "lead",
        header: t("Lead"),
        size: 180,
        Cell: ({ row }) => {
          const l = row.original.lead;
          if (!l) return <span className="text-muted fs-xs">—</span>;
          const name = `${l.name || ""} ${l.surname || ""}`.trim() || l.email || "—";
          return (
            <div className="d-flex flex-column">
              <span className="fs-sm fw-semibold text-truncate" style={{ maxWidth: 160 }} title={name}>
                {name}
              </span>
              {l.customid && (
                <span className="fs-xs text-muted">#{l.customid}</span>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "_eventCount",
        header: t("Events"),
        size: 80,
        enableSorting: false,
        Cell: ({ cell }) => (
          <span className="badge bg-light text-dark border fs-xs">{cell.getValue() || 1}</span>
        ),
      },
      {
        accessorKey: "createdAt",
        header: t("Last Update"),
        size: 160,
        Cell: ({ cell, row }) => {
          const ts = row.original.normalized?.timestamp || cell.getValue();
          return ts ? new Date(ts).toLocaleString() : "—";
        },
      },
    ],
    [t]
  );

  return (
    <div id="content_wrapper">
      <section className="crm-wrapper bg-white pb-5">
        <TabsHeader
          commonHeaderObject={commonHeaderObject}
          activeOption={t("Webhook Data")}
        />

        <div className="container-fluid px-lg-5">
          {/* toolbar */}
          <div className="bg-white mt-3 mb-3">
            <div className="d-flex justify-content-between align-items-center flex-column flex-md-row g-2">
              <div className="ps-1">
                <BreadCrumb breadCrumbText={breadcrumbText} top={false} />
              </div>

              <div className="d-flex justify-content-md-end align-items-center gap-2 flex-wrap">
                <Link
                  to="#"
                  onClick={(e) => { e.preventDefault(); fetchWebhookEvents(); }}
                  className="btn btn-light border d-flex align-items-center px-3 py-2 rounded-3 shadow-sm"
                >
                  <span className="material-symbols-outlined icon-md me-1">refresh</span>
                  <span className="fw-semibold small">{t("Reload")}</span>
                </Link>

                <button
                  className="btn btn-light border d-flex align-items-center gap-2 px-3 py-2 rounded-3 shadow-sm position-relative"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#webhookEventFilter"
                >
                  <span className="material-symbols-outlined icon-md">filter_list</span>
                  <span className="fw-semibold small">{t("Filter")}</span>
                  {activeFilterCount > 0 && (
                    <span
                      className="badge bg-primary rounded-circle position-absolute top-0 start-100 translate-middle"
                      style={{ fontSize: "10px" }}
                    >
                      {activeFilterCount}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* table */}
          <div className="table-wrapper">
            {isLoading ? (
              <LoadingSkeleton />
            ) : (
              <MaterialReactTable
                columns={columns}
                data={groupedEvents}
                getRowId={(row) => row.callId || row._id}
                defaultColumn={{ minSize: 20, maxSize: 300, size: 50 }}
                muiTableContainerProps={{ sx: { maxHeight: "60vh" } }}
                enableStickyHeader
                muiTableBodyRowProps={({ row }) => ({
                  onClick: () => openCallDetails(row.original.callId || row.original._id),
                  sx: { cursor: "pointer" },
                })}
              />
            )}
          </div>
        </div>
      </section>

      <WebhookCallDetailsPanel
        callId={selectedCallId}
        onClose={() => setSelectedCallId(null)}
      />

      <WebhookEventFilterPopup
        phoneFilter={phoneFilter}
        setPhoneFilter={setPhoneFilter}
        eventTypeFilter={eventTypeFilter}
        setEventTypeFilter={setEventTypeFilter}
        providerFilter={providerFilter}
        setProviderFilter={setProviderFilter}
        fromDate={fromDate}
        setFromDate={setFromDate}
        toDate={toDate}
        setToDate={setToDate}
        onApply={handleApplyFilter}
        onReset={handleResetFilter}
      />

      {showAlert && (
        <AlertNotification
          showAlert={showAlert}
          message={alertMessage}
          alertType={messageType}
          onClose={onAlertClose}
        />
      )}
    </div>
  );
};

export default WebhookDataBody;
